import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Anchor, BookmarkPlus, Check, Loader2, Send, Volume2 } from "lucide-react";
import { api, fileUrl, getJourney } from "../lib/api";
import { PageShell, PremiumNotice } from "../components/PageShell";
import { formatApiError, isPremiumError, useAuth } from "../context/AuthContext";

const STARTERS = ["What happened after that decision?", "Are you happy?", "How did you become successful?", "What mistakes did you make?", "Did you regret your choices?", "What should I do differently?"];
const nearestAge = (h) => (h <= 10 ? 10 : h <= 25 ? 25 : 40);

export default function Self() {
  const { sid } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [journey, setJourney] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(1);
  const [locked, setLocked] = useState(false);
  const audio = useRef(null);

  useEffect(() => {
    api.get(`/selves/${sid}`).then(({ data }) => {
      setS(data);
      setMsgs(data.messages || []);
      getJourney(data.journey_id).then(setJourney);
    });
  }, [sid]);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/selves/${sid}/chat`, { message: text });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMsgs((m) => m.slice(0, -1));
      if (isPremiumError(e)) setLocked(true); else toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const speak = async (text) => {
    try {
      const { data } = await api.post(`/selves/${sid}/voice`, { message: text });
      audio.current.src = `data:audio/mp3;base64,${data.audio_base64}`;
      audio.current.play();
    } catch (e) {
      toast.error(isPremiumError(e) ? "Voice conversations are a Premium feature." : formatApiError(e));
    }
  };

  const saveConvo = async () => {
    try { await api.post(`/selves/${sid}/save-conversation`); toast.success("Conversation saved to your journal."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!s) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  const aged = journey?.aged_photos?.[nearestAge(s.horizon)];
  const frags = s.fragments || [];

  return (
    <PageShell back={`/multiverse/${s.journey_id}`} backLabel="Multiverse" label={`Timeline · +${s.horizon} years`} wide>
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="glass rounded-3xl p-6" data-testid="self-card">
            {aged && <figure className="mb-4"><img src={fileUrl(aged)} alt="Artistic simulation of an older you" className="w-28 h-28 rounded-full object-cover border-2 border-amber-300/70" />
              <figcaption className="text-[10px] text-white/40 mt-1">Artistic simulation</figcaption></figure>}
            <div className="font-mono-label text-amber-200/80 mb-1">{s.emotional_theme} · age {s.age}</div>
            <h1 className="text-3xl font-display font-light mb-3">{s.timeline_name}</h1>
            <p className="text-sm text-white/70 leading-relaxed mb-4">{s.story}</p>
            <dl className="text-xs space-y-1.5 text-white/60">
              {[["Career", s.career], ["Finances", s.finances], ["Relationship", s.relationship], ["Family", s.family], ["Perspective", s.emotional_perspective]].map(([k, v]) => (
                <div key={k}><dt className="font-mono-label inline mr-2">{k}</dt><dd className="inline">{v}</dd></div>))}
            </dl>
            <div className="flex flex-wrap gap-1.5 mt-4">{(s.traits || []).map((t) => <span key={t} className="text-[10px] px-2 py-1 rounded-full border border-white/10">{t}</span>)}</div>
          </div>
          <div className="glass rounded-3xl p-6" data-testid="memory-fragments">
            <div className="font-mono-label mb-3">Memory fragments</div>
            {frags.slice(0, revealed).map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, filter: "blur(6px)" }} animate={{ opacity: 1, filter: "blur(0px)" }} className="mb-4">
                <div className="text-xs text-cyan-200/80">{f.kind?.replace("_", " ")} · {f.title}</div>
                <p className="text-sm italic text-white/75">{f.text}</p>
              </motion.div>
            ))}
            {revealed < frags.length && <button onClick={() => setRevealed(revealed + 1)} className="btn-pill text-xs" data-testid="reveal-fragment">Reveal another memory</button>}
          </div>
          <div className="glass rounded-3xl p-6 text-xs text-white/65 space-y-3">
            {[["Accomplishments", s.accomplishments], ["Challenges", s.challenges], ["Lessons", s.lessons]].map(([k, list]) => (
              <div key={k}><div className="font-mono-label mb-1">{k}</div><ul className="list-disc pl-4 space-y-0.5">{(list || []).map((x) => <li key={x}>{x}</li>)}</ul></div>))}
          </div>
        </div>

        <div className="lg:col-span-3 space-y-5">
          <div className="glass rounded-3xl p-6 flex flex-col" data-testid="self-chat">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono-label">Talk to {s.timeline_name}</div>
              <button onClick={saveConvo} className="btn-pill text-xs" data-testid="save-conversation"><BookmarkPlus size={12} /> Save to journal</button>
            </div>
            <div className="space-y-3 max-h-[420px] overflow-y-auto mb-4" data-testid="self-messages">
              {!msgs.length && <p className="text-sm italic text-amber-100/80">"I'm you, {s.horizon} years from now. I remember when we stood exactly where you are. Ask me anything."</p>}
              {msgs.map((m, i) => (
                <div key={i} className={`text-sm rounded-2xl px-4 py-3 leading-relaxed ${m.role === "user" ? "bg-cyan-400/10 border border-cyan-400/25 ml-10" : "border border-amber-300/25 bg-amber-300/[0.04] mr-10"}`}>
                  {m.content}
                  {m.role === "assistant" && <button onClick={() => speak(m.content)} className="ml-2 text-amber-300/70 hover:text-amber-200 align-middle" aria-label="Play voice" data-testid={`voice-${i}`}><Volume2 size={13} /></button>}
                </div>
              ))}
              {busy && <div className="text-xs text-white/50 flex gap-2 items-center"><Loader2 size={12} className="animate-spin" /> Remembering…</div>}
            </div>
            {locked ? <PremiumNotice feature="Expanded conversations" /> : <>
              <div className="flex flex-wrap gap-2 mb-3">{STARTERS.map((q, i) => <button key={q} onClick={() => send(q)} data-testid={`starter-${i}`}
                className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 text-white/60 hover:text-white">{q}</button>)}</div>
              <div className="flex gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send(input)} placeholder="Ask your alternate self…" data-testid="self-chat-input"
                  className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-5 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-300/60" />
                <button onClick={() => send(input)} disabled={busy} className="btn-pill btn-pill-gold" data-testid="self-chat-send"><Send size={14} /></button>
              </div></>}
            <p className="text-[10px] text-white/35 mt-3">A fictional roleplay of a possible life, not facts or predictions.{!user?.is_premium && " Voice replies are Premium."}</p>
          </div>
          {msgs.some((m) => m.role === "assistant") && <RealityAnchor s={s} />}
        </div>
      </div>
      <audio ref={audio} className="hidden" />
    </PageShell>
  );
}

const RealityAnchor = ({ s }) => {
  const [lessons, setLessons] = useState(null);
  const [custom, setCustom] = useState("");
  const [anchor, setAnchor] = useState(null);
  const [update, setUpdate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/journey/${s.journey_id}/anchors`).then(({ data }) => setAnchor(data.find((a) => a.self_id === s.id) || null));
  }, [s]);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const suggest = () => run(async () => setLessons((await api.post(`/selves/${s.id}/lessons`)).data.lessons));
  const create = (lesson) => run(async () => { setAnchor((await api.post(`/selves/${s.id}/anchor`, { message: lesson })).data); toast.success("Lesson anchored into a real-life plan."); });
  const toggle = (field, i) => run(async () => {
    const list = anchor[field].map((x, k) => (k === i ? { ...x, done: !x.done } : x));
    setAnchor((await api.patch(`/anchors/${anchor.id}`, { [field]: list })).data);
  });
  const progress = () => run(async () => {
    const { data } = await api.post(`/anchors/${anchor.id}/progress`, { message: update });
    setAnchor({ ...anchor, updates: [...(anchor.updates || []), data] });
    setUpdate("");
  });

  const all = anchor ? [...anchor.steps, ...anchor.milestones] : [];
  const pct = all.length ? Math.round((all.filter((x) => x.done).length / all.length) * 100) : 0;
  return (
    <div className="glass rounded-3xl p-6" style={{ borderColor: "rgba(255,215,0,0.35)" }} data-testid="reality-anchor">
      <div className="font-mono-label text-amber-200 mb-2 flex items-center gap-2"><Anchor size={12} /> Reality Anchor</div>
      {!anchor ? (<>
        <p className="font-display text-lg mb-4">What lesson from this possible future would you like to bring into your real life?</p>
        {!lessons ? <button onClick={suggest} disabled={busy} className="btn-pill text-xs mb-3" data-testid="anchor-suggest">{busy ? <Loader2 size={12} className="animate-spin" /> : null} Suggest lessons</button>
          : <div className="space-y-2 mb-3">{lessons.map((l, i) => <button key={l} onClick={() => create(l)} disabled={busy} data-testid={`anchor-lesson-${i}`}
            className="block w-full text-left text-sm px-4 py-2.5 rounded-xl border border-white/10 hover:border-amber-300/50">{l}</button>)}</div>}
        <div className="flex gap-2"><input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Or write your own lesson…" data-testid="anchor-custom"
          className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none" />
          <button onClick={() => custom.trim() && create(custom.trim())} disabled={busy} className="btn-pill btn-pill-gold text-xs" data-testid="anchor-create">Anchor it</button></div>
      </>) : (<>
        <p className="font-display text-lg mb-1">{anchor.lesson}</p>
        <div className="h-1.5 bg-white/5 rounded-full mb-4 overflow-hidden"><div className="h-full bg-amber-300" style={{ width: `${pct}%`, transition: "width 0.5s" }} data-testid="anchor-progress-bar" /></div>
        {[["steps", "Three starting steps"], ["milestones", "Weekly milestones"]].map(([f, label]) => (
          <div key={f} className="mb-4"><div className="font-mono-label mb-2">{label}</div>
            {anchor[f].map((x, i) => <button key={i} onClick={() => toggle(f, i)} data-testid={`anchor-${f}-${i}`} className="flex gap-3 items-start text-left w-full py-1.5 text-sm">
              <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${x.done ? "bg-amber-300 border-amber-300" : "border-white/25"}`}>{x.done && <Check size={12} color="#05050A" />}</span>
              <span className={x.done ? "line-through text-white/40" : "text-white/85"}>{f === "milestones" ? `Week ${x.week}: ${x.goal}` : x.text}</span></button>)}
          </div>))}
        {(anchor.updates || []).map((u, i) => <div key={i} className="text-sm mb-3"><p className="text-cyan-100/80">You: {u.text}</p><p className="italic text-amber-100/85">{s.timeline_name}: {u.reply}</p></div>)}
        <div className="flex gap-2"><input value={update} onChange={(e) => setUpdate(e.target.value)} placeholder="Share a progress update…" data-testid="anchor-update-input"
          className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none" />
          <button onClick={() => update.trim() && progress()} disabled={busy} className="btn-pill btn-pill-gold text-xs" data-testid="anchor-update-send">{busy ? <Loader2 size={12} className="animate-spin" /> : "Update"}</button></div>
      </>)}
    </div>
  );
};
